import ErrorIcon from '@mui/icons-material/Error';
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AutocompleteField, {
  AutocompleteItem,
} from '@/components/Common/Field/AutocompleteField';
import FormAutocomplete from '@/components/Common/Form/FormAutocomplete';
import FormTextField from '@/components/Common/Form/FormTextField';
import { useModal } from '@/context/Modal.context';
import { useDynamicDefects } from '@/hooks/defect-details/use-dynamic-defects';
import { useGetBuckets } from '@/hooks/defect-details/use-get-buckets';
import { useDefaultComments } from '@/hooks/use-default-comments';
import { useQcMode } from '@/hooks/use-qcMode';
import { DefectOptionType } from '@/types/interfaces/defect-details';
import { DynamicFormValues } from '../DefectDetails';
import Title from '../Title';

const BasicInfoForm = () => {
  const { t } = useTranslation();

  const [defaultComment, setDefaultComment] = useState<AutocompleteItem | null>(
    null
  );

  const { data: defaultComments } = useDefaultComments('defect');

  const { isViewMode: isPageViewMode } = useQcMode();

  const {
    modal: { modalInfo },
  } = useModal();

  const { checkpointId, viewMode } = modalInfo || {};
  const { preselectedDefectTypeId } = (modalInfo || {}) as {
    preselectedDefectTypeId?: number;
  };

  const { data: buckets, isLoading: bucketLoading } =
    useGetBuckets(checkpointId);

  const { control, setValue } = useFormContext<DynamicFormValues>();
  const selectedDefect = useWatch({ name: 'defect', control });

  const { fields } = useDynamicDefects({
    control,
    requiredFields: selectedDefect?.requiredFields,
    optionalFields: selectedDefect?.optionalFields,
  });

  const defectOptions: DefectOptionType[] = useMemo(() => {
    if (!buckets?.length) return [];

    const defects = buckets.reduce((accumulator, bucket) => {
      if (bucket.defectTypes.length) {
        const bucketDefects = bucket.defectTypes
          .map((f) => ({
            label: f.name,
            id: f.id,
            forceKo: f.forceKo,
            bucketId: bucket.id,
            bucketName: bucket.name,
            requiredFields: bucket.requiredFields,
            optionalFields: bucket.optionalFields,
          }))
          .sort((a, b) => -b.label.localeCompare(a.label));

        accumulator.push(...bucketDefects);
      }
      return accumulator;
    }, [] as DefectOptionType[]);

    return defects.sort((a, b) => -b.bucketName.localeCompare(a.bucketName));
  }, [buckets]);

  useEffect(() => {
    if (preselectedDefectTypeId && defectOptions.length) {
      const match = defectOptions.find((d) => d.id === preselectedDefectTypeId);
      if (match) setValue('defect', match);
    }
  }, [preselectedDefectTypeId, defectOptions]);

  const commentOptions: AutocompleteItem[] = useMemo(() => {
    return defaultComments
      ? defaultComments.map((d) => ({ label: d.name, value: d.code }))
      : [];
  }, [defaultComments]);

  const handleDefaultCommentChange = (
    selectedItem: AutocompleteItem | null
  ) => {
    setDefaultComment(selectedItem);
    if (selectedItem) setValue('comment', selectedItem.label);
  };

  return (
    <Box>
      <Stack flexDirection="row" gap={1.5} alignItems="center">
        <Title name={t('QC_V2.MAIN.BASIC_INFO')} />
        {bucketLoading && <CircularProgress size={18} />}
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <FormAutocomplete
            data-testid="choose_a_defect_combobox"
            control={control}
            name="defect"
            label={t('QC_V2.DEFECT.CHOOSE_DEFECT')}
            rules={{
              required: t('QC_V2.DEFECT.REQUIRED'),
            }}
            autocompleteProps={{
              options: defectOptions,
              isOptionEqualToValue: (option, value) => {
                return option.id === value.id;
              },
              getOptionKey: (opt) => `${opt.id}-${opt.bucketId}`,
              groupBy: (opt) => `${opt.bucketName}`,
              disabled: viewMode || bucketLoading,
            }}
          />

          {!!selectedDefect?.forceKo && (
            <Stack
              sx={{
                marginTop: '0.5rem',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ErrorIcon color="warning" fontSize="small" />
              <Typography
                variant="body2"
                color={(theme) => theme.palette.warning.main}
              >
                {t('QC_V2.DEFECT.DEFECT_FORCE_KO')}
              </Typography>
            </Stack>
          )}
        </Grid>

        {/* Dynamic fields based on api required fields */}
        {fields}

        <Grid item xs={12}>
          <Stack sx={{ gap: 2.5 }}>
            <AutocompleteField
              disabled={isPageViewMode}
              options={commentOptions}
              label={t('QC_V2.COMMON.DEFAULT_COMMENT')}
              value={defaultComment}
              onChange={(_, value) => handleDefaultCommentChange(value)}
              disableClearable
            />
            <FormTextField
              data-testid="defect_comment_textfield"
              control={control}
              name="comment"
              label={t('QC_V2.DEFECT.COMMENT')}
              textFieldProps={{
                multiline: true,
                rows: 6,
                disabled: isPageViewMode,
              }}
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfoForm;
